import { useState } from "react";
import { Phone, Mail, MapPin, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { api, formatApiErrorDetail } from "@/lib/api";

const initial = { name: "", email: "", phone: "", message: "", interested_in_mortgage: false };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Message sent.");
      setDone(true);
      setForm(initial);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send message.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-navy/20 px-4 py-3.5 font-body text-navy placeholder:text-ink-soft/50 focus:outline-none focus:border-brass focus:ring-1 focus:ring-brass transition-colors";

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact Mike Sidhu, Personal Real Estate Corporation | RE/MAX Performance Realty"
        description="Reach Mike Sidhu, Personal Real Estate Corporation, for foreclosure and court-ordered real estate in BC. Call 604-831-2500 or send a message — mortgage referrals available via DLC."
        path="/contact"
      />
      <section className="bg-navy pt-36 pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal><p className="overline">Get in Touch</p></Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-white mt-4 leading-tight">Contact</h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="font-body text-white/70 text-lg mt-6 max-w-2xl leading-relaxed">
              Whether you're a developer, lender, lawyer or homebuyer — reach out and Mike's team will respond promptly.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
          {/* Info */}
          <div className="lg:col-span-4">
            <Reveal>
              <p className="overline mb-6">Direct Lines</p>
              <ul className="space-y-6 font-body text-navy">
                <li className="flex items-start gap-4">
                  <Phone size={20} className="text-brass mt-0.5" />
                  <div><p className="font-medium">Phone</p><p className="text-ink-soft">604-831-2500</p></div>
                </li>
                <li className="flex items-start gap-4">
                  <Mail size={20} className="text-brass mt-0.5" />
                  <div><p className="font-medium">Email</p><p className="text-ink-soft">mike@mikesidhu.com</p></div>
                </li>
                <li className="flex items-start gap-4">
                  <MapPin size={20} className="text-brass mt-0.5" />
                  <div><p className="font-medium">Office</p><p className="text-ink-soft">RE/MAX Performance Realty<br />Surrey, British Columbia</p></div>
                </li>
              </ul>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="mt-10 border-l-2 border-brass pl-5">
                <p className="overline mb-2">Mortgage Cross-Referral</p>
                <p className="font-body text-ink-soft text-sm leading-relaxed">
                  Need financing? Mike partners with a Dominion Lending Centres (DLC) mortgage brokerage
                  to connect buyers with competitive lending options. Check the box below and we'll include a referral.
                </p>
              </div>
            </Reveal>
          </div>

          {/* Form */}
          <div className="lg:col-span-8">
            <Reveal>
              {done ? (
                <div data-testid="contact-success" className="bg-white border border-navy/10 p-12 text-center">
                  <div className="w-14 h-14 bg-brass text-white flex items-center justify-center mx-auto"><Check size={26} /></div>
                  <h3 className="font-heading text-2xl text-navy mt-6">Message received</h3>
                  <p className="font-body text-ink-soft mt-3">Thank you — Mike's team will be in touch shortly.</p>
                  <button onClick={() => setDone(false)} data-testid="contact-send-another" className="mt-8 font-body text-navy border-b border-brass pb-1 hover:text-brass transition-colors">
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="contact-form" className="bg-white border border-navy/10 p-8 md:p-10 space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="overline block mb-2">Name *</label>
                      <input data-testid="contact-name" className={inputCls} value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Full name" />
                    </div>
                    <div>
                      <label className="overline block mb-2">Phone</label>
                      <input data-testid="contact-phone" className={inputCls} value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="(604) 000-0000" />
                    </div>
                  </div>
                  <div>
                    <label className="overline block mb-2">Email *</label>
                    <input data-testid="contact-email" type="email" className={inputCls} value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@email.com" />
                  </div>
                  <div>
                    <label className="overline block mb-2">Message *</label>
                    <textarea data-testid="contact-message" rows={5} className={inputCls} value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Tell Mike about your situation or the property you're interested in…" />
                  </div>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input data-testid="contact-mortgage" type="checkbox" className="mt-1 accent-[#A57F35] w-4 h-4" checked={form.interested_in_mortgage} onChange={(e) => update("interested_in_mortgage", e.target.checked)} />
                    <span className="font-body text-sm text-ink-soft">I'd like a mortgage referral through Mike's DLC brokerage partner.</span>
                  </label>
                  <button data-testid="contact-submit" type="submit" disabled={submitting} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-navy text-white px-8 py-4 hover:bg-brass transition-colors duration-300 disabled:opacity-60">
                    {submitting ? "Sending…" : "Send Message"} {!submitting && <ArrowRight size={17} />}
                  </button>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
